import React, { useState, useMemo } from "react";
import { Button } from "@/race-tracker/components/ui/button";
import { Copy, Check, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/race-tracker/components/ui/tabs";
import SerperOrganicHitList from "./SerperOrganicHitList";
import { normalizeSerperOrganicItem, normalizeSerperNewsItem } from "@/race-tracker/lib/serperQueryCatalog";

function normalizeSerperResultsForList(serperResults) {
  if (!Array.isArray(serperResults)) return { hits: [], stringErrors: [] };
  const stringErrors = [];
  const hits = [];
  serperResults.forEach((row, i) => {
    if (typeof row === "string") {
      stringErrors.push(row);
      return;
    }
    if (!row || typeof row !== "object") return;
    const ep = row.serper_endpoint;
    if (ep === "news") {
      hits.push(
        row.title != null && row.link != null
          ? { ...row, position: row.position != null ? row.position : i + 1 }
          : normalizeSerperNewsItem(row, i)
      );
      return;
    }
    if (ep === "search") {
      hits.push(
        row.title != null && row.link != null
          ? { ...row, position: row.position != null ? row.position : i + 1 }
          : normalizeSerperOrganicItem(row, i)
      );
      return;
    }
    if (row.publisher != null || row.news_date != null) {
      hits.push(normalizeSerperNewsItem(row, i));
      return;
    }
    hits.push(normalizeSerperOrganicItem(row, i));
  });
  return { hits, stringErrors };
}

export default function SynthesisCard({
  synthesis,
  synthesisTimestamp,
  isSweeping,
  serperResults = [],
  serperSkipped = "",
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(synthesis || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const bullets = synthesis
    ? synthesis.split("\n").map((l) => l.trim()).filter((l) => l.startsWith("-") || l.startsWith("•") || l.match(/^\d+\./))
    : [];

  const { hits: serperHits, stringErrors: serperStringErrors } = useMemo(
    () => normalizeSerperResultsForList(serperResults),
    [serperResults]
  );

  const hasSourceSignals =
    serperHits.length > 0 ||
    serperStringErrors.length > 0 ||
    !!(serperSkipped && String(serperSkipped).trim());

  const briefingBody = isSweeping ? (
    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Generating synthesis…
    </div>
  ) : synthesis ? (
    <>
      <ul className="space-y-1.5">
        {bullets.length > 0 ? (
          bullets.map((b, i) => (
            <li key={i} className="text-sm text-foreground flex gap-2">
              <span className="text-primary shrink-0">—</span>
              <span>{b.replace(/^[-•]\s*/, "").replace(/^\d+\.\s*/, "")}</span>
            </li>
          ))
        ) : (
          <li className="text-sm text-foreground whitespace-pre-line">{synthesis}</li>
        )}
      </ul>
      {synthesisTimestamp && (
        <p className="text-[10px] text-muted-foreground">
          Generated {format(new Date(synthesisTimestamp), "MMM d, yyyy 'at' h:mm a")}
        </p>
      )}
    </>
  ) : (
    <p className="text-sm text-muted-foreground">
      Synthesis is not configured.{" "}
      <Link to="/admin/integrations" className="text-primary underline hover:no-underline">
        Go to Integrations
      </Link>{" "}
      to add your Anthropic API key.
    </p>
  );

  const sourcesBody = isSweeping ? (
    <p className="text-xs text-muted-foreground py-2">Sources will refresh when the sweep completes.</p>
  ) : !hasSourceSignals ? (
    <p className="text-xs text-muted-foreground py-2">Run a sweep to populate Serper rows used for the briefing.</p>
  ) : (
    <div className="space-y-5 max-h-[min(420px,55vh)] overflow-y-auto pr-1">
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Serper</p>
        {serperSkipped ? (
          <p className="text-xs text-amber-800 dark:text-amber-500/95 leading-relaxed">{serperSkipped}</p>
        ) : null}
        {serperStringErrors.length > 0 && (
          <ul className="text-xs text-amber-800 dark:text-amber-500/95 list-disc pl-4 space-y-1">
            {serperStringErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        )}
        {serperHits.length > 0 ? <SerperOrganicHitList hits={serperHits} /> : null}
        {!serperSkipped && serperHits.length === 0 && serperStringErrors.length === 0 ? (
          <p className="text-xs text-muted-foreground">No Serper results for this sweep.</p>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border-l-4 border-l-primary bg-blue-50/60 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">Intelligence Briefing</p>
        {synthesis && !isSweeping && (
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 shrink-0" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="w-3 h-3 text-emerald-600" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" /> Copy Summary
              </>
            )}
          </Button>
        )}
      </div>

      <Tabs defaultValue="briefing" className="w-full">
        <TabsList className="h-8 w-full sm:w-auto justify-start">
          <TabsTrigger value="briefing" className="text-xs px-3">
            Briefing
          </TabsTrigger>
          <TabsTrigger value="sources" className="text-xs px-3">
            Sources
          </TabsTrigger>
        </TabsList>
        <TabsContent value="briefing" className="mt-3 focus-visible:outline-none">
          {briefingBody}
        </TabsContent>
        <TabsContent value="sources" className="mt-3 focus-visible:outline-none">
          {sourcesBody}
        </TabsContent>
      </Tabs>
    </div>
  );
}
